/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Reaching the shared expression a row uses** — `X30a` (`#1290`), ratified by Todd 2026-09-10.
 *
 * `X5` made a picked library entry an **address** rather than a copy, and `X30` put its name, its
 * summary and its reach on the row. What was missing was getting to the thing: the library is a
 * separate page, so *what does this actually do, and how do I change it* had no answer from the row
 * that depends on it.
 *
 * Three steps, and the middle one is the whole design:
 *
 * 1. **Open** it. The expression editor, filled and read-only.
 * 2. **Edit** offers two acts, never one — *Edit a copy* or *Edit the shared expression* — by rights.
 * 3. **Override the default** locally, which is `ReferenceDefaultControl` on the row above and is
 *    already built.
 *
 * ## It opens in place, and that is why there is no new rendering here
 *
 * > *"I would expect the read-only editor to behave identically to a locally authored expression —
 * > opening in-line. It's the same UI to the user, just read-only with the edit control."*
 *
 * So this component draws no expression of its own. `X40` already says the row's expansion **is** the
 * editor, and a shared expression is a value in that row like any other — what this adds is the
 * frame around it: who it belongs to, what changing it reaches, and the choice of which thing you
 * are about to change. The read-only rendering is `DecoratorExpressionEditor`'s own `readOnly`,
 * because a bespoke viewer would be a second copy of the editor that drifts from it invisibly.
 *
 * ## The forking risk is answered by naming both acts, not by withholding one
 *
 * `#1290` was filed on the objection that a local copy is *addressed, not copied* undone by a button.
 * True of an **unnamed** button. `TM9` is the ratified answer to exactly this choice — one gesture,
 * two outcomes, both named, *one must never silently do the other* — and it is the vocabulary here.
 *
 * It is **not** `CloneArtifactDialog`, and that is a correction to the ruling as first recorded
 * rather than a departure from it: that dialog authors a **new artifact's identity** — an id, a name,
 * an extend-or-independent relation. A copy taken here becomes this row's own value and is no
 * artifact at all, so every field it asks for would have no answer. The vocabulary transfers; the
 * dialog does not. *Follow your own redirect before shipping it.*
 *
 * ## Editing the shared one is an act on N rows, from a screen showing one
 *
 * So the choice carries the sentence that says so. What it does **not** carry is the reach count:
 * `X30` already put it on the value line, which is on screen whenever this panel is, and a second
 * rendering of one mark is `#1248`'s defect — *state a claim once, at the level it is true at*. The
 * claim that varies here is which rows a save reaches, and that is a sentence rather than a number.
 *
 * Neither carries the **list**: *"Used by only on the library page"* (Todd, 2026-09-10), because a
 * list answers a question the operator is not on this screen to ask.
 */

import React from 'react';
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';

import { DecoratorExpressionEditor } from './DecoratorExpressionEditor';
import type { CapabilityRefused } from './MappingControl';
import type { FieldSuggestionsFn } from './DecoratorExpressionEditor';
import type { ResolveSurfaces } from './shared-expression-usability';
import { copyOfReference, copyWouldDropTheOverride } from './value-line';
import { entryVersion, referenceIsBehind, withReferenceSeenAt } from './shared-expression-version';
import { useRecordRevisit } from './useRecordRevisit';
import { useAuth } from '../../../hooks/useAuth';
import { useExpressionLibrary, useUpdateExpression } from '../../../hooks/useExpressionLibrary';
import type { MacroExpression, ReferenceExpression } from '../../../types/decorator-expression';

export interface SharedExpressionPanelProps {
  /** The address this row holds, with whatever local override and note it carries. */
  reference: ReferenceExpression;
  /** Replace the row's value — how *Edit a copy* lands. */
  onChange: (next: MacroExpression) => void;
  /** Close the panel. The chip that opened it is the other way out. */
  onClose: () => void;
  /**
   * **Point this row at a different shared expression** (Todd, 2026-09-10: *"the user will also need
   * a way to switch which shared expression"*).
   *
   * It is a separate act from *Edit* and from the ✕, and it needs its own control for the reason the
   * pill's click could not stay the picker: a set chip opens **what it holds**, so the route to
   * replacing what it holds has to be somewhere the operator is already looking at what they are
   * replacing. The ✕-then-re-pick round trip Todd named as a workaround is two acts for one
   * intention, and it empties the row in between.
   *
   * The caller closes this panel and opens the picker under the chip (`X28a`), rather than nesting a
   * second live surface inside a read-only one.
   */
  onPickAnother: () => void;
  /** Handed down, never re-derived — the row's answer to *may this surface use the library*. */
  offersLibrary: true | CapabilityRefused | null;
  /** Handed down for the same reason: what a nested reference is marked against (`X18`). */
  resolvesAgainst: ResolveSurfaces | null;
  /** The row's path vocabulary, so a response mapping suggests `response.*` here too. */
  getFieldSuggestions?: FieldSuggestionsFn;
}

/** Which thing the operator is editing. `view` is read-only and is where the panel opens. */
type PanelMode = 'view' | 'choosing' | 'shared';

export const SharedExpressionPanel: React.FC<SharedExpressionPanelProps> = ({
  reference,
  onChange,
  onClose,
  onPickAnother,
  offersLibrary,
  resolvesAgainst,
  getFieldSuggestions,
}) => {
  const { data, isLoading, isError } = useExpressionLibrary();
  const entry = (data ?? []).find((candidate) => candidate.id === reference.ref);
  const { can } = useAuth();
  const update = useUpdateExpression();

  const [mode, setMode] = React.useState<PanelMode>('view');
  const [draft, setDraft] = React.useState<MacroExpression | null>(null);

  const version = entryVersion(entry);
  const behind = referenceIsBehind(reference, version);

  /**
   * **Opening it is the revisit** (`#1302`, ratified by Todd 2026-09-10 among three candidates).
   *
   * The row carries a `changed` mark when the library has moved since it recorded a version, and
   * *"the author should revisit"* is what the mark asks for. This is where that happens, so this is
   * where the new version is recorded — the mark clears by being **looked at**, rather than by an
   * artifact save that had nothing to do with the expression, which would clear the notice with
   * nobody having revisited anything.
   *
   * **Only while the row is behind**, which is what keeps this from writing on every open: opening
   * an unchanged one is a pure read and leaves the form clean. It cannot loop either — the write
   * makes `behind` false, and the effect has nothing left to do.
   *
   * A row that recorded **nothing** stays silent and is not stamped here. Absence means *nobody
   * recorded a version*, and inventing one on a glance would start marking from a moment the author
   * did not choose. Such rows acquire their baseline the way every row does: by being picked, or by
   * having the shared expression saved from here.
   *
   * **The dependency list is deliberately the two facts, not the two objects.** `onChange` and
   * `reference` are new on every render, and the write's own effect — the parent handing back a
   * reference that is no longer behind — is what ends it. Listing them makes the effect re-enter on
   * a caller that does **not** feed the change back, which is every render-and-capture test harness,
   * and there it writes without end. The lint warning is the honest cost of that.
   */
  useRecordRevisit(reference, version, onChange);

  /**
   * **The draft is seeded when the act is chosen, never from a render.** Seeding it from the entry
   * on every render would overwrite what the operator has typed the moment the library query
   * refetches — the same shape as re-syncing a text field from its parsed value, and it would look
   * like the editor rejecting their keystrokes.
   */
  const editShared = () => {
    if (!entry) return;
    setDraft(entry.expression);
    setMode('shared');
  };

  const editCopy = () => {
    if (!entry) return;
    onChange(copyOfReference(reference, entry.expression));
    onClose();
  };

  /**
   * **Three outcomes, never collapsed** — the rule `SharedReadOut` already carries. *Looking*,
   * *the library could not be read* and *it is gone* are different facts, and only the last is bad
   * news: saying *no longer in the library* while the request is in flight sends an operator to
   * re-author something that exists.
   */
  if (isLoading)
    return (
      <Frame>
        <Text fontSize="xs" color="fg.muted">
          Looking this expression up…
        </Text>
      </Frame>
    );
  if (isError)
    return (
      <Frame>
        <Text fontSize="xs" color="fg.muted">
          The library could not be read, so nothing is claimed about this expression. Nothing here has changed.
        </Text>
      </Frame>
    );
  if (!entry)
    return (
      <Frame>
        <Text fontSize="xs" color="warning.ink">
          This expression is no longer in the library, so this value is not sent. Come off it with the ✕ on the{' '}
          <Text as="span" fontFamily="mono">
            Shared …
          </Text>{' '}
          chip, then author a value here or pick another shared one.
        </Text>
      </Frame>
    );

  /**
   * **Two rights, and both have to hold.** The role decides whether this operator edits the library
   * at all; the server's stamp (`canEdit`, `RQ11a`) decides whether *this* entry is theirs to change — a
   * private expression somebody else owns is refused for a different reason and the sentence has to say
   * which.
   */
  const mayEditLibrary = can('expression_library', 'update');
  const mayEditThisOne = entry.canEdit === true;
  const maySaveShared = mayEditLibrary && mayEditThisOne;
  const refusal = !mayEditLibrary
    ? 'Your role cannot change the shared library, so editing here makes a copy on this row.'
    : !mayEditThisOne
      ? 'This shared expression belongs to someone else, so editing here makes a copy on this row.'
      : null;

  const dropsTheOverride = copyWouldDropTheOverride(reference, entry.expression);

  return (
    <Frame>
      <HStack justify="space-between" align="flex-start" gap={3} wrap="wrap">
        <Box minWidth={0}>
          <Text fontSize="sm" fontWeight="600" color="fg.default" truncate>
            {entry.name}
          </Text>
          {entry.description && (
            <Text fontSize="xs" color="fg.muted" truncate>
              {entry.description}
            </Text>
          )}
        </Box>
        <Button size="xs" variant="outline" borderColor="border.default" onClick={onClose}>
          Close
        </Button>
      </HStack>

      {/* **The editor, and only ever this one.** `shapeControl="own"` because the row's own chip row
        above is about the *reference*, not about the shape of the thing it points at — two levels,
        two subjects, and the chips here belong to the expression on screen. */}
      <DecoratorExpressionEditor
        // A shared expression has no destination of its own; each row using it answers.
        emptyValue={null}
        // **The declaration lives here**, on the shared expression — every row using it reads it.
        authorsYield
        expression={mode === 'shared' && draft ? draft : entry.expression}
        onChange={setDraft}
        readOnly={mode !== 'shared'}
        /* Saving is the panel's act in `shared` mode and is refused in the other two, so the
           editor's own promote button is never the control here. Todd, 2026-09-10: *"in the 'Edit
           the shared expression' mode, the 'Save as shared…' button should just be 'Save
           expression'"* — one save, named for what it does to the thing on screen. */
        onSaveToLibrary={null}
        shapeControl="own"
        offersLibrary={offersLibrary}
        resolvesAgainst={resolvesAgainst}
        getFieldSuggestions={getFieldSuggestions}
      />

      {/* **The controls that change the mode sit outside the region the mode disables** — the rule
        this control broke twice in two days (`X29`, then `XS3` slice 3). The editor above is the
        disabled region; every way out of it is here. */}
      {mode === 'view' && behind && (
        <Text fontSize="xs" color="warning.ink">
          This expression has been edited since this row recorded it. What you are looking at is what the row sends now.
        </Text>
      )}

      {mode === 'view' && (
        <HStack gap={2} wrap="wrap">
          <Button
            size="xs"
            variant="outline"
            borderColor="accent.solid"
            color="accent.solid"
            onClick={() => setMode('choosing')}
          >
            Edit…
          </Button>
          <Button size="xs" variant="outline" borderColor="border.default" onClick={onPickAnother}>
            Use a different one…
          </Button>
          <Text fontSize="xs" color="fg.muted">
            Editing is a choice about which thing you change. The ✕ on the{' '}
            <Text as="span" fontFamily="mono">
              Shared …
            </Text>{' '}
            chip comes off it altogether.
          </Text>
        </HStack>
      )}

      {mode === 'choosing' && (
        <VStack
          align="stretch"
          gap={2}
          bg="bg.canvas"
          borderRadius="6px"
          p={3}
          borderWidth="1px"
          borderColor="border.subtle"
        >
          {/* Each act carries the sentence that says what it *does*, not the one that names the
            machinery — `CloneArtifactDialog`'s rule, which is #1045 inside a choice. */}
          <HStack gap={2} align="flex-start">
            <Button size="xs" variant="outline" borderColor="border.default" minW="130px" onClick={editCopy}>
              Edit a copy
            </Button>
            <Text fontSize="xs" color="fg.muted">
              This row gets its own copy and stops tracking the library, so later changes there no longer reach it.
              {dropsTheOverride && ' The default you set on this row cannot travel to that shape, so it is not kept.'}
            </Text>
          </HStack>
          <HStack gap={2} align="flex-start">
            <Button
              size="xs"
              variant="outline"
              borderColor={maySaveShared ? 'accent.solid' : 'border.default'}
              color={maySaveShared ? 'accent.solid' : undefined}
              minW="130px"
              disabled={!maySaveShared}
              onClick={editShared}
            >
              Edit the shared expression
            </Button>
            <Text fontSize="xs" color={maySaveShared ? 'fg.muted' : 'warning.ink'}>
              {refusal ?? 'The change reaches every row that uses this expression, not only this one.'}
            </Text>
          </HStack>
          <HStack justify="flex-end">
            <Button size="xs" variant="ghost" onClick={() => setMode('view')}>
              Cancel
            </Button>
          </HStack>
        </VStack>
      )}

      {mode === 'shared' && (
        <VStack align="stretch" gap={2}>
          <Text fontSize="xs" color="warning.ink">
            You are editing the shared expression. Saving changes it everywhere it is used.
          </Text>
          {update.isError && (
            <Text fontSize="xs" color="error.solid">
              {update.error?.message ?? 'The expression could not be saved. Nothing has changed.'}
            </Text>
          )}
          <HStack justify="flex-end" gap={2}>
            <Button
              size="xs"
              variant="outline"
              borderColor="border.default"
              onClick={() => {
                setDraft(null);
                setMode('view');
                update.reset();
              }}
            >
              Cancel
            </Button>
            <Button
              size="xs"
              bg="accent.solid"
              color="white"
              _hover={{ bg: 'accent.ink' }}
              loading={update.isPending}
              onClick={() => {
                if (!draft) return;
                update.mutate(
                  { id: entry.id, input: { expression: draft } },
                  {
                    onSuccess: (saved) => {
                      // **The operator has just seen this version — they wrote it** (`#1302`). Without
                      // this the row would mark itself `changed` against the edit its own author made
                      // seconds earlier, which is a surface warning about something it watched happen.
                      const next = entryVersion(saved);
                      if (next) onChange(withReferenceSeenAt(reference, next));
                      setDraft(null);
                      setMode('view');
                    },
                  },
                );
              }}
            >
              Save expression
            </Button>
          </HStack>
        </VStack>
      )}
    </Frame>
  );
};

/** The panel's container, so the three lookup outcomes and the panel proper share one frame. */
const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <VStack
    align="stretch"
    gap={3}
    mt={2}
    p={3}
    bg="bg.card"
    borderWidth="1px"
    borderColor="border.default"
    borderRadius="md"
  >
    {children}
  </VStack>
);
